import { useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { BooleanField } from '@refinedev/antd';
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Select,
  Space,
  Table,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import { UserRole } from '@/api/generated/schemas/userRole';
import type { UserOut, UserProfileOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProfileRoleTag, UserRoleTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

// ─── Role editor ──────────────────────────────────────────────────────────────

/** Isolated draft state: parent remounts via `key` when the server role
 * changes so the draft never goes stale. */
const UserRoleEditor = ({ userId, role }: { userId: number; role: string }) => {
  const t = useTranslate();
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const [roleDraft, setRoleDraft] = useState(role);
  const { mutate: patchRole, mutation: roleMutation } = useCustomMutation();

  const roleSelectOptions = (
    Object.entries(UserRole) as [string, string][]
  ).map(([key, val]) => ({
    value: val,
    label: t(`iam.users.roles.${key}`),
  }));

  const handleSaveRole = () => {
    patchRole(
      {
        url: `iam/users/${userId}/role`,
        method: 'patch',
        values: { role: roleDraft },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'iam.users',
            invalidates: ['detail', 'list'],
            id: userId,
          });
          open?.({
            type: 'success',
            message: t('notifications.success'),
            description: t('iam.users.actions.role_change_success'),
          });
        },
      }
    );
  };

  return (
    <Space wrap>
      <Select
        style={{ minWidth: 220 }}
        value={roleDraft}
        onChange={(v) => setRoleDraft(v)}
        options={roleSelectOptions}
        disabled={roleMutation.isPending}
      />
      <Button
        type="primary"
        onClick={handleSaveRole}
        loading={roleMutation.isPending}
        disabled={roleDraft === role}
      >
        {t('iam.users.actions.save_role')}
      </Button>
    </Space>
  );
};

// ─── Basic info ───────────────────────────────────────────────────────────────

const BasicInfoCard = ({
  record,
  canSetRole,
}: {
  record: UserOut;
  canSetRole: boolean;
}) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'username',
      label: t('iam.users.fields.username'),
      children: orDash(record.username),
    },
    {
      key: 'email',
      label: t('iam.users.fields.email'),
      children: orDash(
        record.email ? <Text copyable>{record.email}</Text> : null
      ),
    },
    {
      key: 'first_name',
      label: t('iam.users.fields.first_name'),
      children: orDash(record.first_name),
    },
    {
      key: 'last_name',
      label: t('iam.users.fields.last_name'),
      children: orDash(record.last_name),
    },
    {
      key: 'role',
      label: t('iam.users.fields.role'),
      children: canSetRole ? (
        <UserRoleEditor
          key={`${record.id}-${record.role}`}
          userId={record.id}
          role={record.role}
        />
      ) : (
        <UserRoleTag role={record.role} />
      ),
    },
    {
      key: 'is_active',
      label: t('iam.users.fields.is_active'),
      children: <BooleanField value={record.is_active} />,
    },
    {
      key: 'is_verified',
      label: t('iam.users.fields.is_verified'),
      children: <BooleanField value={record.is_verified} />,
    },
    {
      key: 'is_anonymous',
      label: t('iam.users.fields.is_anonymous'),
      children: <BooleanField value={record.is_anonymous} />,
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Card title={t('base.sections.basic_info')}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2, md: 3 }}
        items={items}
      />
    </Card>
  );
};

// ─── Profiles ─────────────────────────────────────────────────────────────────

const ProfilesCard = ({ record }: { record: UserOut }) => {
  const t = useTranslate();
  const profiles = record.profiles ?? [];

  return (
    <Card title={`${t('iam.users.tabs.profiles')} (${profiles.length})`}>
      <Table<UserProfileOut>
        dataSource={profiles}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      >
        <Table.Column<UserProfileOut>
          dataIndex="id"
          title={t('iam.users.profile_fields.id')}
          width={120}
          render={(value: number) => (
            <ShowIconLink resource="account.profiles" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<UserProfileOut>
          dataIndex="account_id"
          title={t('iam.users.profile_fields.account_id')}
        />
        <Table.Column<UserProfileOut>
          dataIndex="name"
          title={t('iam.users.profile_fields.name')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<UserProfileOut>
          dataIndex="role"
          title={t('iam.users.profile_fields.role')}
          render={(value: string) => <ProfileRoleTag role={value} />}
        />
      </Table>
    </Card>
  );
};

// ─── Tab ──────────────────────────────────────────────────────────────────────

interface UserGeneralTabProps {
  record: UserOut;
  canSetRole: boolean;
}

export const UserGeneralTab = ({ record, canSetRole }: UserGeneralTabProps) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={token.margin}>
      <BasicInfoCard record={record} canSetRole={canSetRole} />
      <ProfilesCard record={record} />
    </Flex>
  );
};
